import React, { useEffect, useState } from "react"
import { i18n } from "i18next"
import { DefaultLanguage } from "@moodys/cre-cpm.constants.localization"
import { Language } from "@moodys/cre-cpm-client-apis.apis.services.auth"
import { platformSharedLocales } from "@moodys/cre-cpm-platform-shared.locales"
import { useAuthContext } from "@moodys/cre-cpm-platform-shared.contexts.auth-context"

export type WithLanguageProps = {
  language?: Language
  i18nInstance?: i18n
}

export const REACT_SHARED_NAMESPACE = "cpm-react-shared"

const withLanguage =
  <T extends WithLanguageProps>(WrappedComponent: React.ComponentType<T>) =>
  ({ ...props }: T): React.ReactElement => {
    const { i18nInstance, userLanguage = DefaultLanguage } = useAuthContext()
    const [i18nReady, setI18nReady] = useState<boolean>(false)

    const addLanguage = (lang: Language): void => {
      i18nInstance.addResourceBundle(
        lang as string,
        REACT_SHARED_NAMESPACE,
        platformSharedLocales[lang]
      )
    }

    const hasLanguageBundle = (): boolean =>
      i18nInstance.hasResourceBundle(userLanguage, REACT_SHARED_NAMESPACE)

    const checkLanguage = () => {
      if (hasLanguageBundle()) {
        setI18nReady(true)
      } else {
        addLanguage(userLanguage)
        checkLanguage()
      }
    }

    useEffect(() => {
      // fallback language
      addLanguage(DefaultLanguage)
    }, [])

    useEffect(() => {
      checkLanguage()
    }, [userLanguage])

    return <>{i18nReady && <WrappedComponent i18nInstance={i18nInstance} {...(props as T)} />}</>
  }

export default withLanguage
